import type {
  FilterApi,
  FilterRule,
  NormalizedFilters,
  FilterBlock,
  PreviousRuleCondition,
  FilterRuleCondition,
  FilterScope,
  OperatorOptions,
  PreviousRule,
  EMSRule,
  ReqResRuleType,
  ModuleApiConfig,
} from 'src/types/filter';
import type {
  TranslationFunction,
  MakeKeyFunction,
} from 'src/types/language-support';
import type {
  SimpleABTVariable,
  DynamicTaskObjectModules,
} from 'src/components/lib/automation-module-registry/automation-module-registry';
import {formatPlural} from 'src/utils/intl';
import {formatToSpecificDateFormat} from 'src/utils/automation';

import uniqueId from 'lodash/uniqueId';
import isEmpty from 'lodash/isEmpty';
import {hashObject} from 'src/utils/index';
import has from 'lodash/has';
import type {FilterState} from 'src/reducers/filter';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';
import {ENTITY_TYPE_EVENT_MAP} from 'src/components/lib/automation-module-registry/utils';

import type {
  DeleteNodeUsageWithId,
  Swimlane,
} from 'src/types/automation-workflow';


export const MAX_MINUTES = 59;
export const MAX_HOURS = 23;
export const MAX_FILTER_TRANSITIONS = 7;
export const MAX_FILTER_BLOCKS = 5;
export const SINGLE_VALUE_OPERATORS = ['eq', 'neq', 'gt', 'lt'] as const;

export const ALLOWS_EMPTY_STRING_OPERATORS = ['in'] as const;
export const ALLOWS_EMPTY_DATE_OPERATORS: string[] = [
  'eq',
  'gt',
  'lt',
  'gte',
  'lte',
];

export const DATE_FILTER_OPTIONS = [
  {
    key: 'now',
    label: 'Now',
  },
  {
    key: 'specific_date',
    label: 'Specific Date',
  },
  {
    key: 'relative_date',
    label: 'Relative to',
  },
] as const;

export const RELATIVE_DAYS_MINUS_OR_PLUS = [
  {
    key: 'minus',
    label: 'Minus (-)',
  },
  {
    key: 'plus',
    label: 'Plus (+)',
  },
] as const;

export const VALUE_NOT_REQUIRED_OPERATORS: string[] = [
  'is_null',
  'is_not_null',
  'empty',
  'is_empty_or_null',
  'is_not_empty_and_not_null',
];

export const ARRAY_OPERATORS: string[] = [
  'in',
  'not_in',
  'contains',
  'ncontains',
  'starts_with',
  'nstarts_with',
  'ends_with',
  'includes_one_of',
  'includes_all_of',
  'excludes_all_of',
];

export const DEFAULT_OPERATOR_OPTION = {
  string: 'eq',
  number: 'gt',
  date: 'gt',
  boolean: true,
  email: 'eq',
  phone: 'eq',
  array: 'includes_one_of',
} as const;

export const OPERATOR_OPTIONS: OperatorOptions = {
  string: [
    {
      value: 'eq',
      label: 'is',
      tooltip: 'status = "placed"',
    },
    {
      value: 'neq',
      label: 'is not',
      tooltip: 'status ≠ "placed"',
    },
    {
      value: 'contains',
      label: 'contains',
      tooltip: 'status contains "placed"',
    },
    {
      value: 'ncontains',
      label: 'does not contain',
      tooltip: 'status does not contain "placed"',
    },
    {
      value: 'in',
      label: 'is one of',
      tooltip: `company name = "Dell" OR company name = "Microsoft"`,
    },
    {
      value: 'not_in',
      label: 'is not one of',
      tooltip: `company name ≠ "Dell" AND company name ≠ "Microsoft"`,
    },
    {
      value: 'is_empty_or_null',
      label: 'is empty/null',
      tooltip: 'status = NULL or status is empty',
    },
    {
      value: 'is_not_empty_and_not_null',
      label: 'is not empty/ not null',
      tooltip: 'status ≠ NULL or status is not empty',
    },
  ],
  number: [
    {
      value: 'eq',
      label: 'is',
      tooltip: 'value = 5',
    },
    {
      value: 'neq',
      label: 'is not',
      tooltip: 'value ≠ 5',
    },
    {
      value: 'gt',
      label: 'is greater than',
      tooltip: 'value > 5',
    },
    {
      value: 'lt',
      label: 'is less than',
      tooltip: 'value < 5',
    },
    {
      value: 'gte',
      label: 'is greater than or equal to',
      tooltip: 'value ≥ 5',
    },
    {
      value: 'lte',
      label: 'is less than or equal to',
      tooltip: 'value ≤ 5',
    },
    // Note: Backend is not developed for these operators yet. Once deeloped just uncomment the below two operators.
    // {
    //   value: 'between_inclusive',
    //   label: 'between (inclusive)',
    //   tooltip: '5 ≤ value ≤ 10',
    // },
    // {
    //   value: 'between_exclusive',
    //   label: 'between (exclusive)',
    //   tooltip: '5 < value < 10',
    // },
    {
      value: 'is_empty_or_null',
      label: 'is empty/null',
      tooltip: 'value = NULL or value is empty',
    },
    {
      value: 'is_not_empty_and_not_null',
      label: 'is not empty/not null',
      tooltip: 'value ≠ NULL and value is not empty',
    },
  ],
  currency: [
    {
      value: 'gt',
      label: 'greater than',
      tooltip: 'value > 5',
    },
    {
      value: 'lt',
      label: 'less than',
      tooltip: 'value < 5',
    },
    {
      value: 'eq',
      label: 'equals',
      tooltip: 'value = 5',
    },
    {
      value: 'neq',
      label: 'not equal to',
      tooltip: 'value ≠ 5',
    },
  ],
  date: [
    {
      value: 'gt',
      label: 'is after',
      tooltip: 'date_added > 2023-06-01 ',
    },
    {
      value: 'lt',
      label: 'is before',
      tooltip: 'date_added < 2023-06-09',
    },
    {
      value: 'eq',
      label: 'is equal to',
      tooltip: 'date_added = 2023-06-09 ',
    },
    {
      value: 'gte',
      label: 'is on or after',
      tooltip: 'date_added >= 2023-06-01',
    },
    {
      value: 'lte',
      label: 'is on or before',
      tooltip: 'date_added is <= 2023-06-09',
    },
    {
      value: 'is_empty_or_null',
      label: 'is empty/null',
      tooltip: 'status = NULL or status is empty',
    },
    {
      value: 'is_not_empty_and_not_null',
      label: 'is not empty/ not null',
      tooltip: 'status ≠ NULL or status is not empty',
    },
  ],

  boolean: [
    {
      value: 'true',
      label: 'is true',
      tooltip: 'candidate/active is true',
    },
    {
      value: 'false',
      label: 'is false',
      tooltip: 'candidate/active is false',
    },
  ],
  email: [
    {
      value: 'eq',
      label: 'is',
      tooltip: 'status = "placed"',
    },
    {
      value: 'neq',
      label: 'is not',
      tooltip: 'status ≠ "placed"',
    },
    {
      value: 'in',
      label: 'is one of',
      tooltip: `company name = "Dell" OR company name = "Microsoft"`,
    },
    {
      value: 'not_in',
      label: 'is not one of',
      tooltip: `company name ≠ "Dell" AND company name ≠ "Microsoft"`,
    },
    {
      value: 'is_empty_or_null',
      label: 'is empty/null',
      tooltip: 'status = NULL or status is empty',
    },
    {
      value: 'is_not_empty_and_not_null',
      label: 'is not empty/not null',
      tooltip: 'status ≠ NULL or status is not empty',
    },
  ],
  phone: [
    {
      value: 'eq',
      label: 'is',
      tooltip: 'number = +12123231324',
    },
    {
      value: 'neq',
      label: 'is not',
      tooltip: 'number ≠ +12123231324',
    },
    {
      value: 'in',
      label: 'is one of',
      tooltip: 'number = "123" or number = "456"',
    },
    {
      value: 'not_in',
      label: 'is not one of',
      tooltip: 'number ≠ "123" or number ≠ "456"',
    },
    {
      value: 'is_empty_or_null',
      label: 'is empty/null',
      tooltip: 'number = NULL or number is empty',
    },
    {
      value: 'is_not_empty_and_not_null',
      label: 'is not empty/ not null',
      tooltip: 'number ≠ NULL or number is not empty',
    },
  ],
  array: [
    {
      value: 'includes_one_of',
      label: 'includes one of',
      tooltip: '',
    },
    {
      value: 'includes_all_of',
      label: 'includes all of',
      tooltip: '',
    },
    {
      value: 'excludes_all_of',
      label: 'excludes all of',
      tooltip: '',
    },
    {
      value: 'is_empty_or_null',
      label: 'is empty/null',
      tooltip: 'status = NULL or status is empty',
    },
    {
      value: 'is_not_empty_and_not_null',
      label: 'is not empty/ not null',
      tooltip: 'status ≠ NULL or status is not empty',
    },
  ],
};

export const MODULE_TYPES_WITH_USER_SELECTION = {
  link_tracking: [], //empty list means all entity types are allowed
  voicebot_init: ['voice_call_completed', 'voice_call_attempted'],
} as const;

type UserSelectableModuleType = keyof typeof MODULE_TYPES_WITH_USER_SELECTION;
type UserSelectableModuleTypeToEventKey =
  typeof MODULE_TYPES_WITH_USER_SELECTION;
type ExtractSelectionValues<T> = T extends readonly (infer U)[] ? U : T;

type UserSelectableModuleEventKey<
  TModuleType extends UserSelectableModuleType = UserSelectableModuleType,
> = ExtractSelectionValues<UserSelectableModuleTypeToEventKey[TModuleType]>;

export const MODULE_TYPES_RESOLVABLE_FROM_PREVIOUS_NODE = [
  'chatbot_trigger',
  'survey_trigger',
  'chatbot_over_sms',
  'meetings_trigger',
] as const;

export const ABT_SCOPE = [
  {key: 'next', label: 'Next'},
  {key: 'indefinitely', label: 'Indefinitely'},
] as const;

export const createFilterBlockIdentifier = (): string => uniqueId('block');
export const getDefaultFilterBlock = (): Map<string, FilterBlock> =>
  new Map([[createFilterBlockIdentifier(), {rules: [], scope: null}]]);

export const isRuleValueEmpty = (rule: FilterRule): boolean => {
  // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'EMSRule | PreviousRule'.
  if (!rule.operator) {
    return false;
  }
  // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'EMSRule | PreviousRule'.
  const valueRequired = !VALUE_NOT_REQUIRED_OPERATORS.includes(rule.operator);
  // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'EMSRule | PreviousRule'.
  const isArrayOperator = ARRAY_OPERATORS.includes(rule.operator);

  if (valueRequired) {
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'.
    return rule.value == null || rule.value === '';
  }

  if (isArrayOperator) {
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'.
    return !(Array.isArray(rule.value) && rule.value.length > 0);
  }

  return false;
};

export const createRuleFieldOperatorHash = (rule: FilterRule): string => {
  // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'operator' does not exist on type 'EMSRule | PreviousRule'.
  const {field, operator} = rule;
  const objectToHash = {
    field,
    operator,
    conditions: [],
  } as const;
  // @ts-expect-error - TS2339 - Property 'conditions' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'conditions' does not exist on type 'EMSRule | PreviousRule'.
  if (rule.conditions && rule.conditions.length > 0) {
    // @ts-expect-error - TS2540 - Cannot assign to 'conditions' because it is a read-only property. | TS2339 - Property 'conditions' does not exist on type 'EMSRule | PreviousRule'.
    objectToHash.conditions = [...rule.conditions];
  }
  const hashObj = hashObject(objectToHash);
  return hashObj;
};

export const createRuleHash = (rule: FilterRule): string => {
  // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'operator' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'.
  const {field, operator, value} = rule;
  let objectToHash: Record<string, any> = {};
  if (rule.type === 'prevNode') {
    objectToHash = {conditions: rule.conditions};
  } else {
    objectToHash = {
      field,
      operator,
      value,
    };
  }
  const hashObj = hashObject(objectToHash);
  return hashObj;
};

export const createRulesHash = (rules: FilterRule[]): string => {
  const hashItem = rules.reduce<Record<string, any>>((acc, rule) => {
    acc[createRuleHash(rule)] = true;
    return acc;
  }, {});
  return hashObject(hashItem);
};

export const validateFilterBlocks = (
  blocks: Map<string, FilterBlock>,
  maxBlocks: number | null | undefined,
  getAbtPickerVariableInfo: (
    field: string,
  ) => SimpleABTVariable | null | undefined,
  getAbtUtilVariableInfo: (
    arg1: string,
    entityType: string,
    id: string,
  ) => SimpleABTVariable | null | undefined,
): {
  blockHashCount: Map<string, number>;
  blockwiseRuleHashCount: Map<string, Map<string, number>>;
  duplicateBlockExists: boolean;
  duplicateRuleExistsInAnyBlock: boolean;
  invalidRuleExistsInAnyBlock: boolean;
  noRuleExists: boolean;
  doBlocksExceedMaxAllowedBlocks: boolean;
  anyScopeIsInvalid: boolean;
  anyScopeContainsInvalidTimespan: boolean;
} => {
  const blockHashCount: Map<string, number> = [...blocks.values()].reduce(
    (acc, block) => {
      if (block.rules.length > 0) {
        const hash = createRulesHash(block.rules);
        const val = acc.get(hash);
        if (val) {
          return acc.set(hash, val + 1);
        }
        acc.set(hash, 1);
      }
      return acc;
    },
    new Map(),
  );
  //duplicate rules will have the same hash. this is a map which stores
  // no of rules having the same hash to detect duplicates
  // map (block id,map(ruleHash,count))
  const blockwiseRuleHashCount = [...blocks.entries()].reduce(
    (outerAccumulator, [blockId, block]: [any, any]) => {
      outerAccumulator.set(
        blockId,
        // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'rule' implicitly has an 'any' type.
        block.rules.reduce((acc, rule) => {
          const hash = createRuleHash(rule);
          const val = acc.get(hash);
          if (val) {
            return acc.set(hash, val + 1);
          }
          return acc.set(hash, 1);
        }, new Map()),
      );
      return outerAccumulator;
    },
    new Map(),
  );

  const duplicateBlockExists = [...blockHashCount.values()].some(
    (count) => count > 1,
  );
  const duplicateRuleExistsInAnyBlock = [
    ...blockwiseRuleHashCount.values(),
  ].some((ruleHashCount) =>
    [...ruleHashCount.values()].some((count) => count > 1),
  );

  const invalidRuleExistsInAnyBlock = [...blocks.values()].some((block) =>
    // @ts-expect-error - TS7030 - Not all code paths return a value.
    block.rules.some((rule) => {
      if (rule.type === 'prevNode') {
        const primaryABTRuleInfo = getPrimaryABTRuleInfo(
          rule.conditions,
          getAbtPickerVariableInfo,
        );
        const eventGroup = getABTEventGroup(primaryABTRuleInfo?.entity_type);
        const eventKey = primaryABTRuleInfo?.key;
        const allowUserSelection =
          eventGroup != null && eventKey != null
            ? checkIfModuleAllowsUserSelection(eventGroup, eventKey)
            : false;
        if (allowUserSelection) {
          // we only validate the metadata field for user selection if
          // the field is 'REFERENCE_ID'.'METADATA' field is not validated
          const userSelectionReferenceFieldName = eventKey
            ? getUserSelectionReferenceFieldNameForEvent(
                eventGroup ?? '',
                eventKey,
              )
            : 'REFERENCE_ID';
          if (
            !userSelectionReferenceFieldNameRequiresValidation(
              userSelectionReferenceFieldName,
            )
          ) {
            //if validation is not required on user selection field, return false
            return false;
          }
          const referenceField = extractFieldFromABTRuleConditions(
            userSelectionReferenceFieldName,
            primaryABTRuleInfo,
            getAbtUtilVariableInfo,
          );

          const userSelectedValue = rule.conditions.find(
            (condition) => condition.field === referenceField,
          )?.value;
          //if the user has not selected a value, validation fails and invalid is true
          if (userSelectedValue == null) {
            return true;
          }
          return false;
        }
        return false;
      }
      if (typeof rule.operator === 'boolean') {
        return false;
      }
      if (!rule.operator) {
        return true;
      }
      if (isRuleValueEmpty(rule)) {
        return true;
      }
      //Todo(Vish): handle zip
    }),
  );

  const noRuleExists = [...blocks.values()].every(
    (block) => block.rules.length === 0,
  );

  const doBlocksExceedMaxAllowedBlocks =
    maxBlocks != null && blocks.size > maxBlocks;

  const anyScopeIsInvalid = [...blocks.values()].some((block) => {
    if (!block.scope) {
      return false;
    }
    const timespan = block.scope.timespan;
    return (
      block.scope.direction === 'next' &&
      (timespan == null ||
        (timespan.days === 0 && timespan.hours === 0 && timespan.minutes === 0))
    );
  });

  const anyScopeContainsInvalidTimespan = [...blocks.values()].some((block) => {
    if (!block.scope || block.scope.direction === 'indefinitely') {
      return false;
    }
    const timespan = block.scope.timespan;
    return timespan == null || timespan.hours > 23 || timespan.minutes > 59;
  });

  return {
    blockHashCount,
    blockwiseRuleHashCount,
    duplicateBlockExists,
    duplicateRuleExistsInAnyBlock,
    invalidRuleExistsInAnyBlock,
    noRuleExists,
    doBlocksExceedMaxAllowedBlocks,
    anyScopeIsInvalid,
    anyScopeContainsInvalidTimespan,
  };
};

export const validateNumberValue = ({
  operator,
  localValue,
  localEndValue,
  minValue,
  maxValue,
}: {
  operator: string;
  localValue: string;
  localEndValue: string;
  minValue?: number;
  maxValue?: number;
}) => {
  const errorMessage = [];

  if (operator === 'between_inclusive' || operator === 'between_exclusive') {
    if (minValue && Number(localValue) < minValue) {
      errorMessage[0] = `Value should be greater than min value`;
    } else if (Number(localValue) > Number(localEndValue)) {
      errorMessage[0] = `Value should be less than end value`;
    }

    if (maxValue && Number(localEndValue) > maxValue) {
      errorMessage[1] = `Value should be less than max value`;
    } else if (Number(localEndValue) < Number(localValue)) {
      errorMessage[1] = `Value should be greater than start value`;
    }
  }
  if (operator === 'eq' || operator === 'neq') {
    if (minValue && Number(localValue) < minValue) {
      errorMessage[0] = `Value must be at least ${minValue}`;
    } else if (maxValue && Number(localValue) > maxValue) {
      errorMessage[0] = `Value must be at most ${maxValue}`;
    }
  }
  if (operator === 'gt') {
    if (maxValue && Number(localValue) >= maxValue) {
      errorMessage[0] = `Value must be less than ${maxValue}`;
    } else if (minValue && Number(localValue) < minValue) {
      errorMessage[0] = `Value must be at least ${minValue}`;
      // if user wants to enter a value less than min value, rather go with "is not null" operator}
    }
  }
  if (operator === 'lt') {
    if (minValue && Number(localValue) <= minValue) {
      errorMessage[0] = `Value must be greater than min limit.`;
    } else if (maxValue && Number(localValue) > maxValue) {
      errorMessage[0] = `Value must be less than max limit. Or use "is not null" operator`;
    }
  }
  if (operator === 'gte') {
    if (minValue && Number(localValue) <= minValue) {
      errorMessage[0] = `Value must be greater than min limit. Or use "is not null" operator`;
    } else if (maxValue && Number(localValue) > maxValue) {
      errorMessage[0] = `Value must be less than max limit`;
    } else if (maxValue && Number(localValue) === maxValue) {
      errorMessage[0] = `Value must be less than max limit. Or use "is" operator`;
    }
  }
  if (operator === 'lte') {
    if (minValue && Number(localValue) === minValue) {
      errorMessage[0] = `Value must be greater than min limit. Or use "is" operator`;
    } else if (minValue && Number(localValue) < minValue) {
      errorMessage[0] = `Value must be greater than min limit`;
    } else if (maxValue && Number(localValue) >= maxValue) {
      errorMessage[0] = `Value must be less than max limit. Or use "is not null" operator`;
    }
  }
  return errorMessage;
};

export const isLocalValueEmpty = (value: string | string[]): boolean => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  return !value;
};
export const isOperatorSwitchIncompatible = (
  newOperator: string | boolean,
  oldOperator?: string | boolean | null,
): boolean => {
  if (newOperator === oldOperator) {
    return false;
  }
  const nonArrayOperators = [
    ...SINGLE_VALUE_OPERATORS,
    ...VALUE_NOT_REQUIRED_OPERATORS,
  ];
  // @ts-expect-error - TS2345 - Argument of type 'string | boolean' is not assignable to parameter of type '"empty" | "eq" | "neq" | "gt" | "lt" | "is_null" | "is_not_null" | "is_empty_or_null" | "is_not_empty_and_not_null"'.
  const isNewOperatorNonArrayOperator = nonArrayOperators.includes(newOperator);
  // @ts-expect-error - TS2345 - Argument of type 'string | boolean | null | undefined' is not assignable to parameter of type '"empty" | "eq" | "neq" | "gt" | "lt" | "is_null" | "is_not_null" | "is_empty_or_null" | "is_not_empty_and_not_null"'.
  const isOldOperatorNonArrayOperator = nonArrayOperators.includes(oldOperator);

  return isNewOperatorNonArrayOperator !== isOldOperatorNonArrayOperator;
};

export const isAllowEmptySupported = (
  dataType: string,
  operator: string | boolean,
): boolean => {
  if (
    ['string', 'email', 'phone'].includes(dataType) &&
    // @ts-expect-error - TS2345 - Argument of type 'string | boolean' is not assignable to parameter of type '"in"'.
    ALLOWS_EMPTY_STRING_OPERATORS.includes(operator)
  ) {
    return true;
  } else if (
    ['date'].includes(dataType) &&
    // @ts-expect-error - TS2345 - Argument of type 'string | boolean' is not assignable to parameter of type '"eq" | "gt" | "lt" | "gte" | "lte"'.
    ALLOWS_EMPTY_DATE_OPERATORS.includes(operator)
  ) {
    return true;
  }
  return false;
};

export const getRelativeDateDisplayBasedOnOffset = (offset: number): string => {
  const absoluteOffset = Math.abs(offset);
  return offset > 0
    ? `${absoluteOffset} days from now`
    : `${absoluteOffset} days ago`;
};

export const getRuleDisplayValue = (
  rule: FilterRule,
  noValueText: string,
  dataType: string,
  type?: string,
  t?: (arg1: string, arg2: string) => string,
): string | string[] => {
  // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'.
  const value = rule.value || rule.value === 0 ? rule.value : null;
  // @ts-expect-error - TS2339 - Property 'offset' does not exist on type 'EMSRule | PreviousRule'. | TS2339 - Property 'offset' does not exist on type 'EMSRule | PreviousRule'.
  const offset = rule.offset ? rule.offset : 0;
  if (Array.isArray(value)) {
    if (type === 'main') {
      const valueCsv = value.length ? value.join(', ') : noValueText;
      /**
       * The text is being displayed inside a button where only 50-90 characters are visible;
       * the rest are truncated. However, large strings sometimes prevent truncation.
       * Slicing the string will make the DOM lighter.
       **/
      return valueCsv.slice(0, 100);
    }
    return value.length > 0 ? value.map(String) : noValueText;
  }
  if (value || value === 0) {
    if (dataType === 'date') {
      const result = offset
        ? getRelativeDateDisplayBasedOnOffset(offset)
        : value === 'now'
        ? value
        : formatToSpecificDateFormat(String(value));
      // @ts-expect-error - TS2339 - Property 'metadata' does not exist on type 'EMSRule | PreviousRule'.
      if (rule.metadata?.include_null === true) {
        return `${result}${
          t ? ` (${t('Empty_Date_Allowed', 'Empty Date Allowed')})` : ''
        }`;
      }
      return result;
    }
    return String(value);
  }
  return noValueText;
};

export const ruleValueToStringRuleValue = (
  value: number | null | undefined | number[] | string | string[] | boolean,
): string[] => {
  if (!value) {
    return [];
  }
  if (value != null) {
    if (Array.isArray(value)) {
      return value.map((v) => v.toString());
    }
    return [value.toString()];
  }
  return [];
};

export const ruleValueToNumberRuleValue = (
  value: number | null | undefined | number[] | string | string[] | boolean,
): string => {
  if (!value && value !== 0) {
    return '';
  }
  if (value != null) {
    if (Array.isArray(value)) {
      return '';
    }
    return '' + value.toString();
  }
  return '';
};

export const ruleValueToDateRuleValue = (
  value?: number | number[] | string | string[] | boolean | null,
): string => {
  if (!value) {
    return 'now';
  }
  if (value != null) {
    if (Array.isArray(value)) {
      return 'now';
    }
    return '' + value.toString();
  }
  return 'now';
};

export const extractAttributeNameFromVariable = (str: string): string => {
  const regexPattern = /(?:[^\\.]|\\.)+(?=\}\}$)/;
  const matches = str.match(regexPattern);
  if (matches && matches[0]) {
    return matches[0].replace(/\\\./g, '.');
  }
  return '';
};

export const doesValueExistInRule = (rules: FilterRule[]): boolean => {
  return rules.some((rule) => has(rule, 'value') && rule.value != null);
};

export const ruleContextMenuActions = ({
  clearAllRuleValues,
  removeBlock,
  rules,
  t,
}: {
  clearAllRuleValues: (() => unknown) | null | undefined;
  removeBlock: (() => unknown) | null | undefined;
  rules: FilterRule[];
  t: (arg1: string, arg2: string) => string;
}): Array<MenuOption> => {
  const options: Array<MenuOption> = [];
  if (
    clearAllRuleValues &&
    typeof clearAllRuleValues === 'function' &&
    doesValueExistInRule(rules)
  ) {
    options.push({
      label: t(
        'Clear_all_entity_data_variables_values',
        'Clear all entity data variables values',
      ),
      key: 'clear',
      iconLeft: 'eraser',
    });
  }
  if (removeBlock && typeof removeBlock === 'function') {
    options.push({
      label: t('Delete_block', 'Delete block'),
      key: 'delete',
      iconLeft: 'trash-can',
    });
  }
  return options;
};

export const convertDateHoursMinutesToSeconds = (
  days: number,
  hours: number,
  minutes: number,
): number => {
  return days * 86400 + hours * 3600 + minutes * 60;
};

const getConditionsFromScope = (scope: any, conditions: any) => {
  const startDateField =
    // @ts-expect-error - TS7031 - Binding element 'field' implicitly has an 'any' type.
    conditions.find(({field}) => field.includes('.start_date'))?.field ?? '';
  const endDateField =
    // @ts-expect-error - TS7031 - Binding element 'field' implicitly has an 'any' type.
    conditions.find(({field}) => field.includes('.end_date'))?.field ?? '';
  let startValue, startOffset, endValue, endOffset;
  if (scope?.timespan == null) {
    startValue = 'now';
    startOffset = 0;
    endValue = 'plus_infinity';
    endOffset = 0;
  } else {
    startValue = 'minus_infinity';
    startOffset = 0;
    endValue = 'now';
    const {days, hours, minutes} = scope.timespan;
    const offsetInSeconds = convertDateHoursMinutesToSeconds(
      days,
      hours,
      minutes,
    );
    endOffset = offsetInSeconds;
  }
  return [
    {
      field: startDateField,
      value: startValue,
      offset: startOffset,
      operator: 'gte',
    },
    {
      field: endDateField,
      value: endValue,
      offset: endOffset,
      operator: 'lte',
    },
  ];
};

const getConditionFromActionIdentifier = (actionValueIdentifier: any) => {
  const {actionField, selectedActionValue} = actionValueIdentifier;
  return {
    field: actionField,
    value: selectedActionValue,
    operator: 'eq',
  };
};

const parseScopeCondition =
  (scope: FilterScope | null | undefined) =>
  (condition: PreviousRuleCondition) => {
    const {field} = condition;
    /*
     * note(vish): Special handling for ABT rule scope conditions
     *
     * This function searches conditions using `.start_date` and `.end_date` field patterns
     * rather than utilizing the standard getAbtPickerVariableInfo and getAbtUtilVariableInfo
     * helper functions (because apparently we can't have nice things TᴖT).
     *
     * This approach is necessary because:
     * 1. These helper functions are not accessible within action-creators
     * 2. Scope calculations cannot be performed at the rule block level since each block
     *    can only have a single scope that must be applied to all contained ABT rules
     *
     * This pattern-based approach is reliable since all ABT rule types consistently
     * include conditions with `.start_date` and `.end_date` fields (thankfully, some
     * things in this world are predictable).
     */
    if (field.includes('.start_date')) {
      const startValue = 'minus_infinity';
      const startOffset = 0;

      return {
        field,
        value: startValue,
        offset: startOffset,
        operator: 'gte',
      };
    }
    if (field.includes('.end_date')) {
      let endValue, endOffset;
      if (scope?.timespan == null) {
        endValue = 'plus_infinity';
        endOffset = 0;
      } else {
        endValue = 'now';
        const {days, hours, minutes} = scope.timespan;
        const offsetInSeconds = convertDateHoursMinutesToSeconds(
          days,
          hours,
          minutes,
        );
        endOffset = offsetInSeconds;
      }
      return {
        field,
        value: endValue,
        offset: endOffset,
        operator: 'lte',
      };
    }
    return condition;
  };

export const normalizeAbtRule = (
  rule: PreviousRule,
  scope?: FilterScope | null,
): Array<FilterRuleCondition | PreviousRuleCondition> => {
  const finalConditions = rule.conditions.map(parseScopeCondition(scope));
  return finalConditions;
};

export const normalizeEMSRule = (
  rule: EMSRule | ReqResRuleType,
): FilterRuleCondition | null | undefined => {
  if (rule.operator != null) {
    let operator = rule.operator;
    let value = rule.value != null && rule.value;
    // @ts-expect-error - TS2345 - Argument of type 'string | true' is not assignable to parameter of type '"empty" | "is_null" | "is_not_null" | "is_empty_or_null" | "is_not_empty_and_not_null"'.
    if (rule.operator && VALUE_NOT_REQUIRED_OPERATORS.includes(rule.operator)) {
      // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'string | number | boolean | string[] | number[]'.
      value = null;
    }
    if (rule.operator === 'in_radius') {
      operator = 'in';
    }
    if (
      typeof rule.operator !== 'undefined' &&
      // @ts-expect-error - TS2345 - Argument of type 'string | boolean' is not assignable to parameter of type 'boolean'.
      [true, false].includes(rule.operator)
    ) {
      operator = 'eq';
      value = rule.operator;
    }
    if (rule.operator === 'empty') {
      operator = 'eq';
      value = '';
    }
    return rule.offset == null
      ? {
          field: rule.field,
          value,
          operator,
          // metadata: rule.operator === 'in_radius' ? {...rule.meta, type: 'distance', operator: 'search'} : null,
          metadata: rule.metadata,
        }
      : {
          field: rule.field,
          value,
          operator,
          // metadata: rule.operator === 'in_radius' ? {...rule.meta, type: 'distance', operator: 'search'} : null,
          //note(vish): we need to convert the offset to seconds
          offset: convertDaysToSeconds(rule.offset),
          metadata: rule.metadata,
        };
  }
  return null;
};

export const normalizeFilter = (filter: FilterState): NormalizedFilters => {
  // @ts-expect-error - TS2869 - Right operand of ?? is unreachable because the left operand is never nullish.
  const blocks = ([...filter.blocks.values()] ?? []).reduce<Array<any>>(
    (acc, block) => {
      const conditions = block.rules.reduce<Array<any>>((innerAcc, rule) => {
        const normalizedRule = ['ems', 'reqRes'].includes(rule.type as string)
          ? // @ts-expect-error - TS2345 - Argument of type 'EMSRule | PreviousRule' is not assignable to parameter of type 'EMSRule'.
            normalizeEMSRule(rule)
          : // @ts-expect-error - TS2345 - Argument of type 'EMSRule | PreviousRule' is not assignable to parameter of type 'PreviousRule'.
            normalizeAbtRule(rule, block.scope);
        if (normalizedRule) {
          if (['ems', 'reqRes'].includes(rule.type as string)) {
            innerAcc.push({conditions: [normalizedRule]});
          } else {
            innerAcc.push({conditions: normalizedRule});
          }
        }
        return innerAcc;
      }, []);
      if (conditions.length > 0) {
        acc.push({rules: conditions});
      }
      return acc;
    },
    [],
  );
  return {conditions: {blocks}};
};

const getClientOperator = (
  rule: PreviousRuleCondition | FilterRuleCondition,
) => {
  if (rule.operator === 'eq' && typeof rule.value === 'boolean') {
    return rule.value;
  }
  if (rule.operator === 'eq' && rule.value === '') {
    return 'empty';
  }
  return rule.operator;
};
export const parseEMSRule = (
  rule: FilterRuleCondition | PreviousRuleCondition,
): EMSRule | ReqResRuleType => {
  const type = getSimpleRuleType(rule);
  //NOTE:(diwakersurya) writing this way to make flow happy
  const rulePayload = {
    id: uniqueId('filterRule'),
    operator: getClientOperator(rule),
    value: rule.value,
    field: rule.field,
    // @ts-expect-error - TS2339 - Property 'offset' does not exist on type 'PreviousRuleCondition | FilterRuleCondition'. | TS2339 - Property 'offset' does not exist on type 'PreviousRuleCondition | FilterRuleCondition'. | TS2339 - Property 'offset' does not exist on type 'PreviousRuleCondition | FilterRuleCondition'.
    offset: rule.offset ? convertSecondsToDays(rule.offset) : rule.offset,
    // @ts-expect-error - TS2339 - Property 'metadata' does not exist on type 'PreviousRuleCondition | FilterRuleCondition'. | TS2339 - Property 'metadata' does not exist on type 'PreviousRuleCondition | FilterRuleCondition'.
    ...(rule.metadata && {metadata: rule.metadata}),
  } as const;
  if (type === 'ems') {
    return {
      ...rulePayload,
      type: 'ems',
    };
  }
  if (type === 'reqRes') {
    return {
      ...rulePayload,
      type: 'reqRes',
    };
  }
  return {
    ...rulePayload,
    type: 'ems',
  };
};

const getSimpleRuleType = (
  rule: PreviousRuleCondition | FilterRuleCondition,
) => {
  //NOTE:(diwakersurya) different type of  keys are created for different rule types
  if (rule.field.includes('automation_previous_node_context_fetch')) {
    return 'reqRes';
  }
  return 'ems';
};

export const parseActionRule = (
  conditions: Array<PreviousRuleCondition | FilterRuleCondition>,
): PreviousRule => {
  const parsedConditions = conditions.map(({field, value, operator}) => {
    return {
      field,
      value:
        //note(vish): these typeof checks are there to satisfy flow
        typeof value === 'string' || typeof value === 'boolean' ? value : '',
      operator: typeof operator === 'string' ? operator : 'eq',
    };
  });
  return {
    conditions: parsedConditions,
    id: uniqueId('filterRule'),
    type: 'prevNode',
  };
};

const getDaysHoursMinutesFromOffset = (
  offset: number,
): {
  days: number;
  hours: number;
  minutes: number;
} => {
  let seconds = offset;

  const days = Math.floor(seconds / (24 * 3600));
  seconds %= 24 * 3600;

  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;

  const minutes = Math.floor(seconds / 60);

  return {days, hours, minutes};
};

export const getScopeFromActionRule = (
  conditions: Array<FilterRuleCondition | PreviousRuleCondition>,
): FilterScope => {
  const scope: FilterScope = {direction: 'indefinitely', timespan: null};
  const startDate = conditions.find((condition) =>
    condition.field.includes('start_date'),
  );
  const endDate = conditions.find((condition) =>
    condition.field.includes('end_date'),
  );
  if (startDate && endDate) {
    // Start date's value can be minus_infinity only when the user provides an offset.
    if (endDate.value === 'now') {
      scope.direction = 'next';
      const {days, hours, minutes} = getDaysHoursMinutesFromOffset(
        // @ts-expect-error - TS2339 - Property 'offset' does not exist on type 'PreviousRuleCondition | FilterRuleCondition'.
        endDate.offset ?? 0,
      );
      scope.timespan = {days, hours, minutes};
    } else if (endDate.value === 'plus_infinity') {
      scope.direction = 'indefinitely';
      scope.timespan = null;
    }
  }
  return scope;
};

export const parseFilter = (
  filter: FilterApi,
): Array<{
  id: number;
  transition_id: number;
  display_name: string;
  blocks: Map<string, FilterBlock>;
  nextNode: string;
}> => {
  const {next_nodes} = filter;
  const blocksArr = next_nodes.map((nextNode, index) => {
    const blocks = nextNode.condition.blocks;
    let parsedBlocks = (blocks ?? []).reduce((acc, block) => {
      const rules: Array<FilterRule> = [];
      let scope = null;
      let scopeParsed = false;
      // @ts-expect-error - TS7030 - Not all code paths return a value.
      (block.rules ?? []).forEach((rule) => {
        if (rule.conditions.length === 0) {
          return [];
        }
        if (rule.conditions.length > 1) {
          rules.push(parseActionRule(rule.conditions));
          if (!scopeParsed) {
            scope = getScopeFromActionRule(rule.conditions);
            scopeParsed = true;
          }
        } else {
          rules.push(parseEMSRule(rule.conditions[0]));
        }
      });
      return acc.set(createFilterBlockIdentifier(), {rules, scope});
    }, new Map());

    if (parsedBlocks.size === 0) {
      parsedBlocks = getDefaultFilterBlock();
    }

    return {
      id: index,
      transition_id: nextNode.transition_id,
      display_name: nextNode.display_name,
      blocks: parsedBlocks,
      nextNode: nextNode.next_node,
    };
  });

  return blocksArr;
};

export const appendExceptionsToFilter = (
  blocksArr: Array<{
    id: number;
    transition_id: number;
    display_name: string;
    blocks: Map<string, FilterBlock>;
    nextNode: string;
  }>,
  hfDeleteInfo: Array<DeleteNodeUsageWithId>,
): Array<{
  id: number;
  transition_id: number;
  display_name: string;
  blocks: Map<string, FilterBlock>;
  nextNode: string;
  hfmodeExceptions: Array<string> | null | undefined;
}> => {
  return blocksArr.map((block) => {
    const filterDeleteInfo = hfDeleteInfo.find(
      ({id: infoId}) => parseInt(infoId, 10) === block.id,
    );
    if (filterDeleteInfo) {
      const hfmodeExceptions = Object.keys(filterDeleteInfo).filter(
        (key) =>
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsageWithId'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsageWithId'.
          typeof filterDeleteInfo[key] === 'boolean' && filterDeleteInfo[key],
      );
      return {
        ...block,
        hfmodeExceptions,
      };
    }
    return {
      ...block,
      hfmodeExceptions: [],
    };
  });
};

export const getFilterRuleText = (
  rule: EMSRule,
  ruleType?: string | null,
  options?: {truncateValues?: boolean; maxVisibleValues?: number},
): string => {
  if (!ruleType) {
    return '';
  }
  const {truncateValues = false, maxVisibleValues = 1} = options ?? {};
  const fieldLabel = rule.field;
  const operatorLabel = OPERATOR_OPTIONS[ruleType].find(
    (operator) => operator.value === String(rule.operator),
  )?.label;
  const showValues =
    rule.operator != null &&
    ruleType !== 'boolean' &&
    // @ts-expect-error - TS2345 - Argument of type 'string | boolean' is not assignable to parameter of type '"empty" | "is_null" | "is_not_null" | "is_empty_or_null" | "is_not_empty_and_not_null"'.
    !VALUE_NOT_REQUIRED_OPERATORS.includes(rule.operator);
  const value =
    getRuleDisplayValue(rule, `Add Values for ${fieldLabel}`, ruleType) ?? '';

  let displayValue: string;
  if (Array.isArray(value)) {
    if (truncateValues && value.length > maxVisibleValues) {
      const visibleValues = value.slice(0, maxVisibleValues).join(', ');
      const remainingCount = value.length - maxVisibleValues;
      displayValue = `${visibleValues}, +${remainingCount} others`;
    } else {
      displayValue = value.join(', ');
    }
  } else {
    displayValue = value;
  }

  return `${fieldLabel} ${operatorLabel ?? ''} ${
    showValues ? displayValue : ''
  }`;
};

// @ts-expect-error - TS7006 - Parameter 'obj' implicitly has an 'any' type.
const getKey = (obj, val: string) =>
  Object.keys(obj).find((key) => obj[key] === val);

export const getABTEventGroup = (
  entityType?: string | null,
): string | null | undefined => {
  return entityType ? getKey(ENTITY_TYPE_EVENT_MAP, entityType) : null;
};

export const getABTEventGroupForPreviousNode = (
  entityType?: string | null,
  previousNodeModules?: Record<any, any> | null,
): string => {
  /*note: here we are doing special handling for chatbot action because chatbot action can either be chatbot_trigger or chatbot_over_sms.
   * This works because in a single task node we can either have chatbot_trigger or chatbot_over_sms.
   * */
  if (entityType === 'chatbot_action') {
    const taskModules = previousNodeModules ?? {};
    for (const module of Object.keys(taskModules)) {
      if (
        taskModules[module].type === 'chatbot_over_sms' ||
        taskModules[module].type === 'chatbot_trigger'
      ) {
        return taskModules[module].type;
      }
    }
  }
  return getABTEventGroup(entityType) ?? '';
};

export const extractPropertyFromPreviousNode = (
  previousNodeModules: Record<any, any> | null | undefined,
  eventGroup: string | null | undefined,
  selectedFieldModuleKey: string,
  property: string,
): Array<string> => {
  if (!previousNodeModules || !eventGroup) {
    return [];
  }
  const result: Array<string> = [];
  const taskModules = previousNodeModules ?? {};
  for (const module of Object.keys(taskModules)) {
    let value = '';
    if (module === selectedFieldModuleKey) {
      if (
        eventGroup === 'chatbot_trigger' ||
        eventGroup === 'chatbot_over_sms'
      ) {
        value = taskModules[module].params[property];
      } else if (
        taskModules[module].type === eventGroup &&
        module === selectedFieldModuleKey
      ) {
        value = taskModules[module].params[property];
      }
      if (typeof value === 'number') {
        value = String(value);
      }
      result.push(value);
    }
  }
  return result;
};

export const ruleBlockContainsABTRule = (rules: FilterRule[]): boolean =>
  rules.some(({type}) => type === 'prevNode');

export const prevNodeSelectableActions = (
  modules?: Record<any, any> | null,
  actionType?: string | null,
): number => {
  //note(vish): if we do not have previous node modules, we'll simply show simple rule line (eg. in case of sms action)
  if (!modules || isEmpty(modules)) {
    return 1;
  }
  let count = 0;
  Object.keys(modules).forEach((module) => {
    const type = modules[module].type;
    if (type === actionType) {
      count += 1;
    }
  });
  return count;
};

export const showRuleLineWithModule = (actionType?: string | null): boolean => {
  // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type '"chatbot_trigger" | "survey_trigger" | "chatbot_over_sms"'.
  return MODULE_TYPES_RESOLVABLE_FROM_PREVIOUS_NODE.includes(actionType);
};

export const getStaticRuleLineText = (
  rule: PreviousRule,
  getPreviousNodeName: (arg1: string) => string,
  getAbtPickerVariableInfo: (
    field: string,
  ) => SimpleABTVariable | null | undefined,
  ruleActionType: string,
  getChatbotName?: ((flowId: number) => string) | null,
  getSurveyName?: ((surveySchemaId: number) => string) | null,
  getMeetingTemplateName?: ((templateId: string) => string) | null,
  nodeId?: string | null,
  referenceId?: string | null,
): string => {
  const primaryABTRuleCondition = getPrimaryABTRuleCondition(
    rule.conditions,
    getAbtPickerVariableInfo,
  );
  const nodeName = nodeId ? getPreviousNodeName(nodeId) : '';
  const eventName = primaryABTRuleCondition?.field
    ? getAbtPickerVariableInfo(primaryABTRuleCondition.field)?.title ?? ''
    : '';
  const eventValue = primaryABTRuleCondition?.value;
  let ruleLineText = '';
  switch (ruleActionType) {
    case 'email_send':
    case 'sms_send':
    case 'whatsapp_send':
    case 'smart_scheduler':
    case 'voicebot_trigger':
      ruleLineText = `${nodeName} - ${eventName} is ${String(eventValue)}`;
      break;
    case 'voicebot_init':
      ruleLineText = `${nodeName} - ${eventName} is ${String(eventValue)}`;
      break;
    case 'chatbot_over_sms':
    case 'chatbot_trigger':
      let chatbotName = '';
      if (referenceId) {
        chatbotName = getChatbotName?.(parseInt(referenceId, 10)) ?? '';
      }
      ruleLineText = `${nodeName} - ${eventName} for ${chatbotName} is ${String(
        eventValue,
      )}`;
      break;
    case 'survey_trigger':
      let surveyName = '';
      if (referenceId) {
        surveyName = getSurveyName?.(parseInt(referenceId, 10)) ?? '';
      }
      ruleLineText = `${nodeName} - ${eventName} for ${surveyName} is ${String(
        eventValue,
      )}`;
      break;
    case 'link_tracking':
      ruleLineText = `${nodeName} - ${eventName} is ${String(eventValue)}`;
      break;
    case 'meetings_trigger':
      let meetingTemplateName = '';
      if (referenceId) {
        meetingTemplateName =
          getMeetingTemplateName?.(String(referenceId)) ?? '';
      }
      ruleLineText = `${nodeName} - ${eventName} ${
        meetingTemplateName ? `for ${meetingTemplateName}` : ''
      } is ${String(eventValue)}`;
      break;
  }
  return ruleLineText;
};

export const getFilterTransitionHeader = (totalTransitions: number): string => {
  return `Path - ${String(totalTransitions)}`;
};

export const getLatestFilterTransitionInfo = (
  nodeId: string,
  swimlanes: Array<Swimlane>,
  swimlaneId: string,
): {
  tid: string;
  transitionId: number;
} => {
  // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'string' and 'number' have no overlap.
  const swimlane = swimlanes.find(({id}) => id === parseInt(swimlaneId, 10));
  const transitionArray = swimlane?.nodes[nodeId].next_nodes ?? [];
  const transitionId = transitionArray.length - 1;
  return {tid: transitionArray[transitionId].next_node, transitionId};
};

export const getRuleActionType = (
  fieldId: string,
  pickerVariables: Map<string, SimpleABTVariable>,
): string | null | undefined => {
  const eventObject = pickerVariables.get(fieldId);
  return getABTEventGroup(eventObject?.entity_type);
};

export const getPartialStaticRuleLineText = (
  prependText: string,
  rule: FilterRule,
  getPreviousNodeName: (arg1: string) => string,
  t: TranslationFunction,
  primaryABTRuleInfo?: SimpleABTVariable | null,
  isDynamic: boolean = false,
  ruleNodeId?: string | null,
): string => {
  const nodeId = ruleNodeId ?? '';
  const nodeName = nodeId ? getPreviousNodeName(nodeId) : '';
  const eventName = primaryABTRuleInfo?.title ?? '';
  return `${String(prependText)}${
    prependText ? ' ' : ''
  }${nodeName} - ${eventName}${isDynamic ? ` ${t('for', 'for')}` : ''}`;
};

export const getActionRulePossibleOptions = (
  rule: FilterRule,
  t: TranslationFunction,
  makeKey: MakeKeyFunction,
  primaryABTRuleInfo?: SimpleABTVariable | null,
): Array<MenuOption> => {
  if (primaryABTRuleInfo) {
    return primaryABTRuleInfo.possible_values.map((val) => ({
      key: String(val),
      label: `${t('is', 'is')} ${t(makeKey(String(val)), String(val))}`,
    }));
  }
  return [];
};

export const getScopeErrorText = (timespan?: string | null): string => {
  if (timespan == null || timespan === '0') {
    return 'Invalid Value';
  }
  return '';
};

export const getDynamicRulesOptions = (
  eventGroup: string,
  dynamicOptions: Array<string>,
  getChatbotName: (flowId: number) => string,
): Array<MenuOption> => {
  let result: Array<MenuOption> = [];
  switch (eventGroup) {
    //note: can add more event groups in the future
    case 'chatbot_over_sms':
    case 'chatbot_trigger':
      result = dynamicOptions.map((item) => ({
        key: item,
        label: getChatbotName(parseInt(item, 10)),
      }));
  }
  return result;
};

export const getNodeIdentifierField = (eventGroup?: string | null): string => {
  return eventGroup === 'sms_send' || eventGroup === 'whatsapp_send'
    ? 'sub_reference_id'
    : 'node_id';
};

export const getReqResRuleLineText = (
  rule: FilterRule,
  dataType: string,
): string => {
  const operatorLabel = OPERATOR_OPTIONS[dataType].find(
    // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'EMSRule | PreviousRule'.
    (operator) => operator.value === String(rule.operator),
  )?.label;
  const showValue =
    dataType !== 'boolean' &&
    // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'EMSRule | PreviousRule'.
    !VALUE_NOT_REQUIRED_OPERATORS.includes(rule.operator);
  // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EMSRule | PreviousRule'.
  const displayValue = showValue ? String(rule.value) : '';
  // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'EMSRule | PreviousRule'.
  return `${rule.field ?? ''} ${operatorLabel ?? ''} ${displayValue}`;
};
export const getDateFilterOptions = (
  operator: string,
  filterRuleType: 'split' | 'filter',
): Array<MenuOption> => {
  //NOTE:(diwakersurya) in split, we don't show relative date
  // because as of now, it will complicate the validation logic on
  // the ui for else if conditions
  if (filterRuleType === 'split') {
    return DATE_FILTER_OPTIONS.filter(
      (option) => option.key !== 'relative_date',
    );
  }
  //NOTE:(diwakersurya) in path, we show relative date based on operator
  if (filterRuleType === 'filter') {
    return ['lt', 'gt', 'eq', 'gte', 'lte'].includes(operator)
      ? // @ts-expect-error - TS4104 - The type 'readonly [{ readonly key: "now"; readonly label: "Now"; }, { readonly key: "specific_date"; readonly label: "Specific Date"; }, { readonly key: "relative_date"; readonly label: "Relative to"; }]' is 'readonly' and cannot be assigned to the mutable type 'MenuOption[]'.
        DATE_FILTER_OPTIONS
      : DATE_FILTER_OPTIONS.filter((option) => option.key !== 'relative_date');
  }
  return [];
};

export const convertDaysToSeconds = (days: number): number => {
  return days * 86400;
};

export const convertSecondsToDays = (seconds: number): number => {
  return seconds / 86400;
};

export const getTimeDescription = ({
  days = 0,
  hours = 0,
  minutes = 0,
}: {
  days: number;
  hours: number;
  minutes: number;
}): string => {
  const parts: Array<string> = [];

  if (days) {
    parts.push(`${days} ${formatPlural(days, 'day', 'days')}`);
  }

  if (hours) {
    parts.push(`${hours} ${formatPlural(hours, 'hour', 'hours')}`);
  }

  if (minutes) {
    parts.push(`${minutes} ${formatPlural(minutes, 'minute', 'minutes')}`);
  }

  if (parts.length === 0) {
    return '';
  }

  if (parts.length === 1) {
    return parts[0];
  }
  if (parts.length === 2) {
    return parts.join(' and ');
  }
  return `${parts[0]}, ${parts[1]} and ${parts[2]}`;
};

type CheckIfModuleAllowsUserSelection = {
  <TModuleType extends UserSelectableModuleType>(
    moduleType: TModuleType,
    eventKey: UserSelectableModuleEventKey<TModuleType>,
  ): boolean;
  (moduleType: string, eventKey: string): boolean;
};

export const checkIfModuleAllowsUserSelection: CheckIfModuleAllowsUserSelection =
  (moduleType: string, eventKey: string) => {
    if (!moduleType) {
      return false;
    }
    if (!(moduleType in MODULE_TYPES_WITH_USER_SELECTION)) {
      return false;
    }
    const allowed = MODULE_TYPES_WITH_USER_SELECTION[
      moduleType as UserSelectableModuleType
    ] as readonly string[];

    //if the module type has empty allowed list, it means the module type allows all entity types
    if (allowed.length === 0) {
      return true;
    }
    return allowed.includes(eventKey);
  };

export const MODULE_API_CONFIGS: {
  [moduleType: string]: ModuleApiConfig;
} = {
  link_tracking: {
    getPathname: () => 'available-urls',
    getQuery: (compositeNodeId) => ({composite_node_id: compositeNodeId}),
    transformResponse: (apiResponse) => {
      if (apiResponse && Array.isArray(apiResponse.urls)) {
        return apiResponse.urls.map((item) => ({
          key: String(item.hash),
          label: String(item.url),
        }));
      }
      return [];
    },
  },
  //note(vish): Example for a future moduleType that might need user selection:
  /*'another_module_type': {
    getPathname: (compositeNodeId, rule) => `another/path/${compositeNodeId}`,
    getQuery: (compositeNodeId, rule) => ({ ruleId: rule.id, type: 'options' }),
    transformResponse: (apiResponse) => {
      if (apiResponse && Array.isArray(apiResponse.data)) {
        return apiResponse.data.map((item) => ({
          key: String(item.value),
          label: String(item.displayName),
        }));
      }
      return [];
    },
  },
  */
};

export const getPrimaryABTRuleCondition = (
  conditions: Array<PreviousRuleCondition>,
  getAbtPickerVariableInfo: (
    field: string,
  ) => SimpleABTVariable | null | undefined,
): PreviousRuleCondition | null | undefined => {
  return (
    conditions.find(({field}) => getAbtPickerVariableInfo(field) != null) ??
    null
  );
};

export const getPrimaryABTRuleInfo = (
  conditions: Array<PreviousRuleCondition>,
  getAbtPickerVariableInfo: (
    field: string,
  ) => SimpleABTVariable | null | undefined,
): SimpleABTVariable | null | undefined => {
  const primaryABTRuleCondition = getPrimaryABTRuleCondition(
    conditions,
    getAbtPickerVariableInfo,
  );
  if (!primaryABTRuleCondition) {
    return null;
  }
  return getAbtPickerVariableInfo(primaryABTRuleCondition.field);
};

//note(vish): mapping needed for parsing action rule conditions
export const ABT_ACTION_VALUE_MAPPING = {
  email_send: {
    REFERENCE_TYPE: 'reference_type',
    REFERENCE_ID: 'reference_id',
    WORKFLOW_ID: 'workflow_id',
    NODE_ID: 'node_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
  },
  //note(vish): chatbot_trigger and chatbot_over_sms are same
  chatbot_trigger: {
    REFERENCE_TYPE: 'reference_type',
    REFERENCE_ID: 'reference_id',
    WORKFLOW_ID: 'workflow_id',
    NODE_ID: 'node_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
  },
  voicebot_trigger: {
    REFERENCE_TYPE: 'reference_type',
    REFERENCE_ID: 'reference_id',
    WORKFLOW_ID: 'workflow_id',
    NODE_ID: 'node_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
    SUB_REFERENCE_TYPE: 'sub_reference_type',
    SUB_REFERENCE_ID: 'sub_reference_id',
  },
  survey_trigger: {
    REFERENCE_TYPE: 'reference_type',
    REFERENCE_ID: 'reference_id',
    WORKFLOW_ID: 'workflow_id',
    NODE_ID: 'node_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
  },
  link_tracking: {
    REFERENCE_TYPE: 'reference_type',
    REFERENCE_ID: 'reference_id',
    WORKFLOW_ID: 'workflow_id',
    NODE_ID: 'node_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
  },
  sms_send: {
    REFERENCE_TYPE: 'reference_type',
    WORKFLOW_ID: 'reference_id',
    NODE_ID: 'sub_reference_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
    SUB_REFERENCE_TYPE: 'sub_reference_type',
  },
  whatsapp_send: {
    REFERENCE_TYPE: 'reference_type',
    WORKFLOW_ID: 'reference_id',
    NODE_ID: 'sub_reference_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
    SUB_REFERENCE_TYPE: 'sub_reference_type',
  },
  voicebot_init: {
    REFERENCE_TYPE: 'reference_type',
    REFERENCE_ID: 'reference_id',
    WORKFLOW_ID: 'workflow_id',
    NODE_ID: 'node_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
    SUB_REFERENCE_TYPE: 'sub_reference_type',
    SUB_REFERENCE_ID: 'sub_reference_id',
    METADATA: 'voice_call_status_metadata',
  },
  meetings_trigger: {
    REFERENCE_TYPE: 'reference_type',
    REFERENCE_ID: 'reference_id',
    WORKFLOW_ID: 'workflow_id',
    NODE_ID: 'node_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
  },
  smart_scheduler: {
    REFERENCE_TYPE: 'reference_type',
    WORKFLOW_ID: 'reference_id',
    NODE_ID: 'sub_reference_id',
    START_DATE: 'start_date',
    END_DATE: 'end_date',
    SUB_REFERENCE_TYPE: 'sub_reference_type',
  },
} as const;

const REFERENCE_CONDITIONS_VALUES_MAPPING = {
  sms_send: {
    REFERENCE_ID: null,
    REFERENCE_TYPE: 'workflow',
    SUB_REFERENCE_TYPE: 'node',
  },
  whatsapp_send: {
    REFERENCE_ID: null,
    REFERENCE_TYPE: 'workflow',
    SUB_REFERENCE_TYPE: 'node',
  },
  email_send: {
    REFERENCE_ID: 'task_definition_id',
    REFERENCE_TYPE: 'task_definition_id',
  },
  chatbot_trigger: {
    REFERENCE_ID: 'flow_id',
    REFERENCE_TYPE: 'chatbot_flow_id',
  },
  chatbot_over_sms: {
    REFERENCE_ID: 'flow_id',
    REFERENCE_TYPE: 'chatbot_flow_id',
  },
  voicebot_trigger: {
    REFERENCE_ID: 'retell_agent_id',
    REFERENCE_TYPE: 'voice_bot_id',
    SUB_REFERENCE_ID: 'node_id',
    SUB_REFERENCE_TYPE: 'node',
  },
  survey_trigger: {
    REFERENCE_ID: 'survey_schema_id',
    REFERENCE_TYPE: 'survey_schema_id',
  },
  link_tracking: {
    REFERENCE_ID: null,
    REFERENCE_TYPE: 'hash',
  },
  voicebot_init: {
    REFERENCE_ID: 'retell_agent_id',
    REFERENCE_TYPE: 'voice_bot_id',
    SUB_REFERENCE_ID: 'node_id',
    SUB_REFERENCE_TYPE: 'node',
  },
  meetings_trigger: {
    REFERENCE_ID: 'template_id',
    REFERENCE_TYPE: 'meeting_template',
  },
  smart_scheduler: {
    REFERENCE_ID: null,
    REFERENCE_TYPE: 'workflow',
    SUB_REFERENCE_TYPE: 'node',
  },
} as const;

export const extractValueFromABTRuleConditions = (
  key: string,
  conditions: Array<PreviousRuleCondition>,
  primaryABTRuleInfo: SimpleABTVariable | null | undefined,
  getAbtUtilVariableInfo: (
    arg1: string,
    arg2: string,
    arg3: string,
  ) => SimpleABTVariable | null | undefined,
): string | null | undefined | boolean | null | undefined => {
  const utilInfo = extractUtilInfoForABTRuleCondition(
    key,
    primaryABTRuleInfo,
    getAbtUtilVariableInfo,
  );
  if (!utilInfo) {
    return null;
  }
  return (
    conditions.find(({field}) => field === utilInfo.fieldPath)?.value ?? null
  );
};

export const extractFieldFromABTRuleConditions = (
  key: string,
  primaryABTRuleInfo: SimpleABTVariable | null | undefined,
  getAbtUtilVariableInfo: (
    arg1: string,
    arg2: string,
    arg3: string,
  ) => SimpleABTVariable | null | undefined,
): string | null | undefined => {
  return (
    extractUtilInfoForABTRuleCondition(
      key,
      primaryABTRuleInfo,
      getAbtUtilVariableInfo,
    )?.fieldPath ?? null
  );
};

export const getUpdatedPrimaryABTRuleCondition = (
  primaryABTRuleCondition: PreviousRuleCondition,
  value: boolean,
): PreviousRuleCondition => {
  return {
    ...primaryABTRuleCondition,
    value,
  };
};

export const extractUtilInfoForABTRuleCondition = (
  key: string,
  primaryABTRuleInfo: SimpleABTVariable | null | undefined,
  getAbtUtilVariableInfo: (
    arg1: string,
    arg2: string,
    arg3: string,
  ) => SimpleABTVariable | null | undefined,
): SimpleABTVariable | null | undefined => {
  if (!primaryABTRuleInfo) {
    return null;
  }
  const {entity_type, fieldGroup} = primaryABTRuleInfo;
  const eventGroup = getABTEventGroup(entity_type);
  if (!eventGroup || !fieldGroup) {
    return null;
  }
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_send: { readonly REFERENCE_TYPE: "reference_type"; readonly REFERENCE_ID: "reference_id"; readonly WORKFLOW_ID: "workflow_id"; readonly NODE_ID: "node_id"; readonly START_DATE: "start_date"; readonly END_DATE: "end_date"; }; ... 5 more ...; readonly whatsapp_send: { ...; }; }'.
  const actionValueMapping = ABT_ACTION_VALUE_MAPPING[eventGroup];
  const value = actionValueMapping[key];
  if (!value) {
    return null;
  }

  return getAbtUtilVariableInfo(fieldGroup, entity_type, value);
};

export const getSecondaryConditionsForABTRule = ({
  field,
  workflowId,
  nodeId,
  moduleKey,
  getAbtUtilVariableInfo,
  getAbtPickerVariableInfo,
  previousNodeModules,
}: {
  field: string;
  workflowId: string;
  nodeId: string | null | undefined;
  moduleKey: string;
  getAbtUtilVariableInfo: (
    arg1: string,
    arg2: string,
    arg3: string,
  ) => SimpleABTVariable | null | undefined;
  getAbtPickerVariableInfo: (
    arg1: string,
  ) => SimpleABTVariable | null | undefined;
  previousNodeModules: DynamicTaskObjectModules | null | undefined;
}): Array<PreviousRuleCondition> => {
  const primaryABTRuleInfo = getAbtPickerVariableInfo(field);
  if (!primaryABTRuleInfo) {
    return [];
  }
  const {entity_type, fieldGroup} = primaryABTRuleInfo;
  const eventGroup = getABTEventGroup(entity_type);
  if (!eventGroup || !fieldGroup) {
    return [];
  }
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_send: { readonly REFERENCE_TYPE: "reference_type"; readonly REFERENCE_ID: "reference_id"; readonly WORKFLOW_ID: "workflow_id"; readonly NODE_ID: "node_id"; readonly START_DATE: "start_date"; readonly END_DATE: "end_date"; }; ... 5 more ...; readonly whatsapp_send: { ...; }; }'.
  const actionValueMapping = ABT_ACTION_VALUE_MAPPING[eventGroup];
  const conditions: Array<PreviousRuleCondition> = [];
  Object.keys(actionValueMapping).forEach((key) => {
    const value = actionValueMapping[key];
    const conditionField = getAbtUtilVariableInfo(
      fieldGroup,
      entity_type,
      value,
    )?.fieldPath;
    if (!conditionField) {
      return;
    }
    const conditionValue = getConditionValueForABTCondition({
      eventGroup,
      key,
      workflowId,
      nodeId,
      moduleKey,
      previousNodeModules,
    });
    conditions.push({
      field: conditionField,
      value: conditionValue,
      operator: 'eq',
    });
  });
  return conditions;
};

const getConditionValueForABTCondition = ({
  // @ts-expect-error - TS7031 - Binding element 'eventGroup' implicitly has an 'any' type.
  eventGroup,
  // @ts-expect-error - TS7031 - Binding element 'key' implicitly has an 'any' type.
  key,
  // @ts-expect-error - TS7031 - Binding element 'workflowId' implicitly has an 'any' type.
  workflowId,
  // @ts-expect-error - TS7031 - Binding element 'nodeId' implicitly has an 'any' type.
  nodeId,
  // @ts-expect-error - TS7031 - Binding element 'moduleKey' implicitly has an 'any' type.
  moduleKey,
  // @ts-expect-error - TS7031 - Binding element 'previousNodeModules' implicitly has an 'any' type.
  previousNodeModules,
}) => {
  if (key === 'WORKFLOW_ID') {
    return workflowId;
  }
  if (key === 'NODE_ID') {
    return nodeId;
  }
  if (key === 'START_DATE' || key === 'END_DATE') {
    return null;
  }
  const referenceConditionsValuesMapping =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly sms_send: { readonly REFERENCE_ID: null; readonly REFERENCE_TYPE: "workflow"; readonly SUB_REFERENCE_TYPE: "node"; }; readonly whatsapp_send: { readonly REFERENCE_ID: null; readonly REFERENCE_TYPE: "workflow"; readonly SUB_REFERENCE_TYPE: "node"; }; ... 5 more ...; readonly link_tracking: { ...; }; }'.
    REFERENCE_CONDITIONS_VALUES_MAPPING[eventGroup];
  const value = referenceConditionsValuesMapping[key];
  if (key === 'REFERENCE_ID') {
    if (value != null) {
      return extractPropertyFromPreviousNode(
        previousNodeModules,
        eventGroup,
        moduleKey,
        value,
      )[0];
    }
  }
  if (key === 'REFERENCE_TYPE' || key === 'SUB_REFERENCE_TYPE') {
    return value;
  }
  if (value === 'node_id') {
    return nodeId;
  }
  return null;
};

const SURVEY_QUESTION_PATH_STRING = 'survey_trigger_conversation_fetch';

export const checkIfSurveyQuestionRule = (rule: ReqResRuleType): boolean => {
  return rule.field?.includes(SURVEY_QUESTION_PATH_STRING);
};

export const GetVoiceCallAttemptedMetadataOptions = (
  t: TranslationFunction,
) => {
  return [
    {value: 'link_sent', label: t('Link_Sent', 'Link Sent')},
    {
      value: 'connection_failed',
      label: t('Connection_Failed', 'Connection Failed'),
    },
    {value: 'no_response', label: t('No_Response', 'No Response')},
    {value: 'voice_mail', label: t('Voice_Mail', 'Voice Mail')},
    {
      value: 'number_not_correct',
      label: t('Number_Incorrect', 'Number Incorrect'),
    },
    {
      value: 'no_response_but_picked_up',
      label: t('Picked_Up_and_Disconnected', 'Picked Up and Disconnected'),
    },
  ];
};
export const GetVoiceCallCompletedMetadataOptions = (
  t: TranslationFunction,
) => {
  return [
    {
      value: 'identification_not_successful',
      label: t('Identification_Unsuccessful', 'Identification Unsuccessful'),
    },
    {
      value: 'number_incorrect',
      label: t('Number_Incorrect', 'Number Incorrect'),
    },
    {value: 'consent_revoked', label: t('Consent_Revoked', 'Consent Revoked')},
    {
      value: 'opportunity_mismatch',
      label: t('Opportunity_Mismatch', 'Opportunity Mismatch'),
    },
    {
      value: 'ready_for_evaluation',
      label: t('Ready_for_Evaluation', 'Ready for Evaluation'),
    },
    {
      value: 'evaluation_not_needed',
      label: t('Evaluation_Not_Required', 'Evaluation Not Required'),
    },
    {
      value: 'max_retries_reached',
      label: t('Max_Retries_Reached', 'Max Retries Reached'),
    },
    {
      value: 'abrupt_termination',
      label: t('Abrupt_Termination', 'Abrupt Termination'),
    },
  ];
};

export const staticModuleEventOptionsMap: Record<
  string,
  (t: TranslationFunction) => Array<MenuOption>
> = {
  voice_call_attempted: (t: TranslationFunction) =>
    GetVoiceCallAttemptedMetadataOptions(t).map((option) => ({
      key: option.value,
      label: option.label,
    })),
  voice_call_completed: (t: TranslationFunction) =>
    GetVoiceCallCompletedMetadataOptions(t).map((option) => ({
      key: option.value,
      label: option.label,
    })),
};

export const USER_SELECTED_FIELDS_REQUIRING_METADATA_VALIDATION = [
  'REFERENCE_ID',
];

export const getUserSelectionReferenceFieldNameForEvent = (
  eventGroup: string,
  eventKey: string,
): 'REFERENCE_ID' | 'METADATA' => {
  // NOTE:(diwakersurya) this field tells which ABT_ACTION_VALUE_MAPPING key is used for sending user selected metadata to backend
  // check ABT_ACTION_VALUE_MAPPING for the Name of REFERENCE_ID and METADATA
  if (!eventGroup || !eventKey) {
    return 'REFERENCE_ID';
  }
  if (eventGroup === 'voicebot_init') {
    if (
      eventKey === 'voice_call_attempted' ||
      eventKey === 'voice_call_completed'
    ) {
      return 'METADATA';
    }
  }
  return 'REFERENCE_ID';
};

//NOTE:(diwakersurya) in future this won't suffice. we will need to comeup with some other configs
// to make the rule UI work with more than 1 UI elements, data, their backend fields and validation logic
export const userSelectionReferenceFieldNameRequiresValidation = (
  UserSelectionReferenceFieldNameForEvent: 'REFERENCE_ID' | 'METADATA',
): boolean => {
  return USER_SELECTED_FIELDS_REQUIRING_METADATA_VALIDATION.includes(
    UserSelectionReferenceFieldNameForEvent,
  );
};

export const cleanupRulesBeforeSave = (
  rules: FilterRule[],
  getAbtPickerVariableInfo: (
    field: string,
  ) => SimpleABTVariable | null | undefined,
  getAbtUtilVariableInfo: (
    arg1: string,
    entityType: string,
    id: string,
  ) => SimpleABTVariable | null | undefined,
): FilterRule[] => {
  const newRules = rules.map((rule) => {
    if (rule.type === 'prevNode') {
      const primaryABTRuleInfo = getPrimaryABTRuleInfo(
        rule.conditions,
        getAbtPickerVariableInfo,
      );
      const eventGroup = getABTEventGroup(primaryABTRuleInfo?.entity_type);
      const eventKey = primaryABTRuleInfo?.key;
      const allowUserSelection =
        eventGroup != null && eventKey != null
          ? checkIfModuleAllowsUserSelection(eventGroup, eventKey)
          : false;
      if (allowUserSelection) {
        // we only validate the metadata field for user selection if
        // the field is 'REFERENCE_ID'.'METADATA' field is not validated
        const userSelectionReferenceFieldName = eventKey
          ? getUserSelectionReferenceFieldNameForEvent(
              eventGroup ?? '',
              eventKey,
            )
          : 'REFERENCE_ID';

        const referenceField = extractFieldFromABTRuleConditions(
          userSelectionReferenceFieldName,
          primaryABTRuleInfo,
          getAbtUtilVariableInfo,
        );
        const userSelectedValue = rule.conditions.find(
          (condition) => condition.field === referenceField,
        )?.value;
        // remove the metadata rules which have value as null
        if (
          (userSelectedValue == null || userSelectedValue === '') &&
          userSelectionReferenceFieldName === 'METADATA'
        ) {
          const newRuleWithoutMetadataCondition = {
            ...rule,
            conditions: rule.conditions.filter(
              (condition) => condition.field !== referenceField,
            ),
          };
          return newRuleWithoutMetadataCondition;
        }
        return rule;
      }
      return rule;
    }
    return rule;
  });
  return newRules;
};

export const getDirectionLabel = (direction: string): string =>
  ABT_SCOPE.find((item) => item.key === direction)?.label ?? '';

export const getFilterNodeTitle = (
  blocksSize: number,
  t: TranslationFunction,
): string => {
  if (blocksSize > 1) {
    return `${t('Path', 'Path')} (${blocksSize} ${formatPlural(
      blocksSize,
      t('OR_Block', 'OR Block'),
      t('OR_Blocks', 'OR Blocks'),
    )})`;
  }
  return t('Path', 'Path');
};

export const getFilterNodeDescription = (
  hasMultipleBlocks: boolean,
  totalRulesCount: number,
  rulesLength: number,
  t: TranslationFunction,
): string => {
  if (hasMultipleBlocks && totalRulesCount > 0) {
    return t(
      'If_any_of_the_following_OR_conditions_are_satisfied',
      'If any of the following OR conditions are satisfied',
    );
  }
  if (rulesLength > 0) {
    return t(
      'If_all_the_following_conditions_are_satisfied',
      'If all the following conditions are satisfied',
    );
  }
  return t('Add_filter_conditions', 'Add filter conditions');
};

export const getFilterContextMenuOptions = (
  hasNameEdit: boolean,
  totalFilterItems: number,
  t: TranslationFunction,
): Array<{key: string; label: string; iconLeft: string}> => [
  ...(hasNameEdit
    ? [
        {
          key: 'rename',
          label: t('Rename_Node', 'Rename Node'),
          iconLeft: 'text',
        },
      ]
    : []),
  {
    key: 'delete',
    label: t('Delete_this_path', 'Delete this path'),
    iconLeft: 'trash',
  },
  ...(totalFilterItems > 1
    ? [
        {
          key: 'delete-all',
          label: t('Delete_all_paths', 'Delete all paths'),
          iconLeft: 'trash-list',
        },
      ]
    : []),
];
